import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// Route name === permission-tab key: гейт = auth.can(`${name}:read`).
const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: () => import('./pages/LoginPage.vue') },
  { path: '/', redirect: '/stats' },
  { path: '/stats', name: 'stats', component: () => import('./pages/StatsPage.vue') },
  { path: '/debug', name: 'debug', component: () => import('./pages/DebugPage.vue') },
  { path: '/moderator', name: 'moderator', component: () => import('./pages/ModeratorPage.vue') },
  { path: '/garage', name: 'garage', component: () => import('./pages/GaragePage.vue') },
  { path: '/garage-halls', name: 'garage-halls', component: () => import('./pages/GarageHallsPage.vue') },
  // Старые адреса: гараж и боевые настройки слились в один экран.
  { path: '/items', redirect: '/garage' },
  { path: '/hulls', redirect: '/garage' },
  { path: '/chat', name: 'chat', component: () => import('./pages/ChatPage.vue') },
  { path: '/bans', name: 'bans', component: () => import('./pages/BansPage.vue') },
  { path: '/news', name: 'news', component: () => import('./pages/NewsPage.vue') },
  { path: '/players', name: 'players', component: () => import('./pages/PlayersPage.vue') },
  { path: '/account-requests', name: 'account-requests', component: () => import('./pages/AccountRequestsPage.vue') },
  { path: '/player-roles', name: 'player-roles', component: () => import('./pages/PlayerRolesPage.vue') },
  { path: '/clans', name: 'clans', component: () => import('./pages/ClansPage.vue') },
  { path: '/battles', name: 'battles', component: () => import('./pages/BattlesPage.vue') },
  { path: '/replays', name: 'replays', component: () => import('./pages/ReplaysPage.vue') },
  { path: '/matchmaking', name: 'matchmaking', component: () => import('./pages/MatchmakingPage.vue') },
  { path: '/economy', name: 'economy', component: () => import('./pages/EconomyPage.vue') },
  { path: '/events', name: 'events', component: () => import('./pages/EventsPage.vue') },
  { path: '/forum', name: 'forum', component: () => import('./pages/ForumPage.vue') },
  { path: '/site', name: 'site', component: () => import('./pages/SitePage.vue') },
  { path: '/maps', name: 'maps', component: () => import('./pages/MapsPage.vue') },
  { path: '/map-presets', name: 'map-presets', component: () => import('./pages/MapPresetsPage.vue') },
  { path: '/map-editor', name: 'map-editor', component: () => import('./pages/MapEditorPage.vue') },
  { path: '/resources', name: 'resources', component: () => import('./pages/ResourcesPage.vue') },
  { path: '/shop', name: 'shop', component: () => import('./pages/ShopPage.vue') },
  { path: '/streamers', name: 'streamers', component: () => import('./pages/StreamersPage.vue') },
  { path: '/promo-codes', name: 'promo-codes', component: () => import('./pages/PromoCodesPage.vue') },
  { path: '/containers', name: 'containers', component: () => import('./pages/ContainersPage.vue') },
  { path: '/kits', name: 'kits', component: () => import('./pages/KitsPage.vue') },
  { path: '/devices', name: 'devices', component: () => import('./pages/DevicesPage.vue') },
  { path: '/overdrives', name: 'overdrives', component: () => import('./pages/OverdrivesPage.vue') },
  { path: '/invites', name: 'invites', component: () => import('./pages/InvitesPage.vue') },
  { path: '/bots', name: 'bots', component: () => import('./pages/BotsPage.vue') },
  { path: '/appearance', name: 'appearance', component: () => import('./pages/AppearancePage.vue') },
  { path: '/battle-royale', name: 'battle-royale', component: () => import('./pages/BattleRoyalePage.vue') },
  { path: '/global-settings', name: 'global-settings', component: () => import('./pages/GlobalSettingsPage.vue') },
  { path: '/servers', name: 'servers', component: () => import('./pages/ServersPage.vue') },
  // Старый адрес раздела Gifts (переименован в Global Settings).
  { path: '/gifts', redirect: '/global-settings' },
  { path: '/quests', name: 'quests', component: () => import('./pages/QuestsPage.vue') },
  { path: '/challenges', name: 'challenges', component: () => import('./pages/ChallengesPage.vue') },
  { path: '/users', name: 'users', component: () => import('./pages/UsersPage.vue') },
  { path: '/admin-activity', name: 'admin-activity', component: () => import('./pages/AdminActivityPage.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Порядок соответствует сайдбару (по алфавиту) — для выбора первой доступной табы.
const TAB_ORDER = [
  'account-requests', 'admin-activity', 'appearance', 'bans', 'battle-royale', 'battles', 'bots', 'challenges', 'chat', 'clans', 'containers', 'debug', 'devices', 'economy', 'events', 'forum', 'garage',
  'garage-halls', 'global-settings', 'invites', 'kits', 'map-editor', 'map-presets', 'maps', 'matchmaking', 'quests',
  'moderator', 'news', 'overdrives', 'player-roles', 'players', 'promo-codes', 'replays', 'resources', 'servers', 'shop', 'site', 'stats', 'streamers', 'users',
];

/**
 * Имя маршрута обычно совпадает с правом (`${name}:read`), но Garage слил два прежних
 * экрана и остался под правом items. Боевые настройки внутри него по-прежнему гейтятся
 * правом hulls — уже на уровне вкладок, см. GaragePage.
 */
const ROUTE_PERM: Record<string, string> = {
  garage: 'items', kits: 'items', devices: 'items', 'garage-halls': 'items', 'player-roles': 'players', overdrives: 'hulls',
};
const permOf = (tab: string) => ROUTE_PERM[tab] ?? tab;

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (to.name === 'login') {
    return auth.isAuthenticated ? { name: 'stats' } : true;
  }

  if (!auth.isAuthenticated) {
    return { name: 'login' };
  }

  // Подтягиваем профиль (права) один раз после перезагрузки страницы.
  if (!auth.user) {
    try {
      await auth.fetchMe();
    } catch {
      auth.clear();
      return { name: 'login' };
    }
  }

  const tab = to.name as string;
  if (TAB_ORDER.includes(tab) && !auth.can(`${permOf(tab)}:read`)) {
    const firstAllowed = TAB_ORDER.find((t) => auth.can(`${permOf(t)}:read`));
    return firstAllowed ? { name: firstAllowed } : true;
  }
  return true;
});
